/**
 * Cutscenes on screen (docs/archive/OPENING.md section 4.4): the client half of `world.scene`.
 *
 * Every frame this reads the scene the World is playing (`{ id, t, waiting }`) and draws it as a
 * function of (data, t) through render/scene.ts: the camera and puppets go to the renderer as its
 * `stage`, ink panels onto their own canvas, and the letterbox, fades, captions, dialogue box, wait
 * prompt and skip button into the DOM. The player only answers: a click, a tap, Space, Enter or the
 * gamepad's A sends `sceneAdvance`; the skip button, holding Esc or holding the gamepad's B or Start
 * sends `sceneSkip`. Nothing here changes the world.
 *
 * After a scene, the "go here" marker (`world.story.guide`) points the way until it is reached.
 */
import './cutscene.css';
import {
  bySex,
  EMOJI_SECONDS,
  PLAYER_NAME,
  screenFacing,
  screenHeading,
  SCENES,
  type Activity,
  type Entity,
  type PlayerCommand,
  type SceneDef,
  type Spot,
  type World,
  type WorldEvent,
} from '@nine/shared';
import type { Sfx, Theme } from '../audio';
import type { IsoCamera } from '../render/camera';
import type { Renderer, Stage } from '../render/renderer';
import {
  cuesCrossed,
  musicAt,
  SCENE_POSES,
  sceneFrame,
  typed,
  waitPrompt,
  type SceneFrame,
  type StageHost,
} from '../render/scene';
import { drawPanel, PANEL_H, PANEL_W, type PanelCast } from '../render/scenePanels';
import { drawPortrait, type SpriteBank } from '../render/sprites';
import { playSfx } from '../sfx';
import { settings } from '../settings';
import { h, setText } from './dom';
import { showRecap } from './sceneRecap';

/** Holding Esc (or the gamepad's B / Start) this long skips the scene. */
const HOLD_TO_SKIP = 1;
const FADE_COLORS = { black: '#050407', white: '#f6f1e6', ink: '#1c191d' } as const;
/** Captions ease in and out over this long (seconds). */
const CAPTION_EASE = 0.45;

/** The sound a scene's `sfx` cue plays (audio.ts, or a registered sfx id: sfx/sounds/world.ts). */
const SOUNDS: Record<string, (s: Sfx) => void> = {
  gong: (s) => s.gongHit(),
  waves: (s) => s.waves(),
  hit: (s) => s.hit(),
  hurt: (s) => s.hurt(),
  ching: (s) => s.ching(),
  nine: (s) => s.nine(),
  heartbeat: (s) => s.heartbeat(),
  whisper: (s) => s.whisper(),
  boom: (s) => s.boom(),
  charge: (s) => s.charge(),
  land: (s) => s.land(),
  palmGlow: () => void playSfx('scene.palmGlow'),
  shrineHum: () => void playSfx('scene.shrineHum'),
  // The plains opening (data/plainsOpening.ts; sounds in sfx/sounds/story.ts).
  rain: () => void playSfx('scene.rain'),
  letter: () => void playSfx('scene.letter'),
  lights: () => void playSfx('scene.lights'),
  flash: () => void playSfx('scene.flash'),
  bird: () => void playSfx('amb.bird'),
};

/** With the screen flashes setting off, a white flash only half covers the picture. */
const SOFT_WHITE = 0.45;

export class Cutscene {
  private readonly root: HTMLDivElement;
  private readonly paper: HTMLDivElement;
  private readonly panelCanvas: HTMLCanvasElement;
  private readonly fade: HTMLDivElement;
  private readonly captions: HTMLDivElement;
  private readonly lineBox: HTMLDivElement;
  private readonly face: HTMLCanvasElement;
  private readonly nameEl: HTMLDivElement;
  private readonly textEl: HTMLDivElement;
  private readonly waitEl: HTMLDivElement;
  private readonly holdBar: HTMLElement;
  private readonly guideEl: HTMLDivElement;
  private readonly guideArrow: HTMLElement;
  /** The scene shown last frame and its clock then (sounds fire as the clock passes them). */
  private shownId: string | null = null;
  private lastT = -1;
  /** Whose portrait the dialogue box shows. */
  private faceOf = '';
  /** Esc / gamepad held to skip: when it went down (seconds, real time), or null. */
  private holdStart: number | null = null;
  private padPrev: boolean[] = [];
  /** The music the scene asks for, or null (the game's own then). */
  music: Theme | null = null;

  constructor(
    private readonly world: World,
    private readonly renderer: Renderer,
    private readonly sprites: SpriteBank,
    private readonly sfx: Sfx,
    private readonly send: (c: PlayerCommand) => void,
    /** Whether the player is in the world yet (not on the title screens). */
    private readonly playing: () => boolean,
    /** A scene began: close whatever windows are open. */
    private readonly onStart: () => void,
    /** Whether a window (a panel, the map, the settings) is open over the world. */
    private readonly windowOpen: () => boolean = () => false,
  ) {
    this.panelCanvas = h('canvas', { width: PANEL_W, height: PANEL_H });
    this.paper = h('div', { class: 'cs-paper' }, this.panelCanvas);
    this.fade = h('div', { class: 'cs-fade' });
    this.captions = h('div', { class: 'cs-captions' });
    this.face = h('canvas', { class: 'cs-face', width: 56, height: 56 });
    this.nameEl = h('div', { class: 'cs-name' });
    this.textEl = h('div', { class: 'cs-text' });
    this.lineBox = h(
      'div',
      { class: 'cs-line', hidden: true },
      this.face,
      h('div', {}, this.nameEl, this.textEl),
      h('span', { class: 'cs-more' }, '▼'),
    );
    this.waitEl = h('div', { class: 'cs-wait', hidden: true });
    this.holdBar = h('i', { class: 'cs-hold' });
    const skip = h(
      'button',
      { type: 'button', class: 'btn ghost small cs-skip', title: 'ข้ามฉากนี้ (กด Esc ค้าง)' },
      'ข้าม ',
      h('small', {}, 'Esc'),
      this.holdBar,
    );
    skip.addEventListener('pointerdown', (e) => e.stopPropagation());
    skip.addEventListener('click', (e) => {
      e.stopPropagation();
      this.skip();
    });
    this.root = h(
      'div',
      { id: 'cutscene', hidden: true },
      this.paper,
      this.fade,
      h('div', { class: 'cs-bar top' }),
      h('div', { class: 'cs-bar bottom' }),
      this.captions,
      this.lineBox,
      this.waitEl,
      skip,
    );
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'ฉากเนื้อเรื่อง');
    this.root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.advance();
    });
    this.root.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
    this.root.addEventListener('contextmenu', (e) => e.preventDefault());
    this.guideArrow = h('i');
    this.guideEl = h('div', { id: 'csGuide', hidden: true }, h('span', {}, 'ไปที่นี่'), this.guideArrow);
    document.body.append(this.root);
    // Scripted stills (scripts, review captures): jump the scene under way to a time (dev builds only).
    if (import.meta.env.DEV)
      (window as unknown as { __scene?: unknown }).__scene = {
        seek: (t: number) => {
          const st = this.world.scene as { t: number; waiting: boolean } | null;
          if (st) Object.assign(st, { t, waiting: false });
        },
        get: () => this.world.scene,
      };
    // The marker belongs to the world view: under the HUD and every window (theme.css z-layers).
    const hud = document.getElementById('hud');
    if (hud?.parentElement) hud.parentElement.insertBefore(this.guideEl, hud);
    else document.body.append(this.guideEl);
    // Keys reach the scene first: nothing else hears a key pressed while it plays (key releases
    // still pass, so the game never thinks a key is held).
    addEventListener('keydown', (e) => this.onKey(e), { capture: true });
    addEventListener('keyup', (e) => {
      if (e.code === 'Escape') this.holdStart = null;
    });
  }

  /** Real time in seconds: holding to skip takes a second however slow the frames are. */
  private now(): number {
    return performance.now() / 1000;
  }

  /** Whether a scene is on screen. */
  get active(): boolean {
    return this.playing() && this.world.scene !== null;
  }

  handle(ev: WorldEvent): void {
    if (ev.type !== 'scene') return;
    if (ev.phase === 'start') {
      this.lastT = -1;
      this.shownId = null;
      this.onStart();
    }
    // The plains opening skipped: what it told, in three lines (data/plainsOpening.ts OPENING_RECAP).
    if (ev.phase === 'end' && ev.skipped && ev.id === 'plains-opening') showRecap();
  }

  /** Once per frame, before the world is drawn. */
  sync(): void {
    const s = this.world.scene;
    const def = s ? SCENES[s.id] : undefined;
    if (!s || !def || !this.playing()) {
      this.close();
      this.syncGuide();
      return;
    }
    this.guideEl.hidden = true;
    this.pollPad();
    if (this.holdStart != null) {
      const k = (this.now() - this.holdStart) / HOLD_TO_SKIP;
      this.holdBar.style.width = `${Math.min(100, k * 100)}%`;
      if (k >= 1) {
        this.holdStart = null;
        this.skip();
      }
    } else this.holdBar.style.width = '0';
    if (this.shownId !== s.id) {
      this.shownId = s.id;
      this.faceOf = '';
      this.lastT = -1;
    }
    // Sounds and music as the clock passes their cues.
    for (const c of cuesCrossed(def, this.lastT, s.t)) {
      if (c.do === 'sfx') SOUNDS[c.sfx]?.(this.sfx);
    }
    this.lastT = s.t;
    const music = musicAt(def, s.t);
    this.music = (music as Theme | null) ?? (def.kind === 'panels' ? 'none' : null);

    const f = sceneFrame(def, s.t, this.host());
    this.root.hidden = false;
    document.body.classList.add('in-cutscene');
    this.root.classList.toggle('panels', def.kind === 'panels');
    this.root.classList.toggle('stage', def.kind === 'stage');
    this.root.classList.toggle('white', f.fade.color === 'white' && f.fade.a > 0.5);
    this.drawPanels(def, f);
    this.drawFade(f);
    this.drawCaptions(f);
    this.drawLine(f);
    const prompt = s.waiting ? waitPrompt(def, s.t) : null;
    this.waitEl.hidden = !prompt;
    if (prompt) setText(this.waitEl, prompt);
    this.stage(def, f);
  }

  // ───────────────────────── answering ─────────────────────────

  private advance(): void {
    if (this.active) this.send({ t: 'sceneAdvance' });
  }

  private skip(): void {
    if (this.active) this.send({ t: 'sceneSkip' });
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.active) return;
    // Browser shortcuts (reload, dev tools) still work.
    if (e.ctrlKey || e.metaKey || e.altKey || /^F\d+$/.test(e.key)) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    if (e.repeat) return;
    if (e.code === 'Space' || e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'KeyZ')
      this.advance();
    else if (e.code === 'Escape') this.holdStart = this.now();
  }

  /** The gamepad while a scene plays: A answers, B or Start held skips. */
  private pollPad(): void {
    const pad = [...(navigator.getGamepads?.() ?? [])].find((p) => p?.connected);
    if (!pad) return;
    const down = pad.buttons.map((b) => b.pressed);
    const pressed = (i: number) => !!down[i] && !this.padPrev[i];
    if (pressed(0)) this.advance();
    const held = !!down[1] || !!down[9];
    if (held && this.holdStart == null && (pressed(1) || pressed(9))) this.holdStart = this.now();
    if (!held && this.padPrev.some((v, i) => v && (i === 1 || i === 9))) this.holdStart = null;
    this.padPrev = down;
  }

  // ───────────────────────── drawing ─────────────────────────

  private host(): StageHost {
    const w = this.world;
    return {
      point: (spot: Spot) => w.story.point(spot),
      home: (actor: string) => {
        const e = this.entityOf(this.def(), actor);
        // The player stands where they were when the scene began (the World moves them at the end
        // of a walk; the puppet walks there first).
        if (!e || e === w.player) return w.story.point({ at: 'player' });
        return { x: e.x, y: e.y };
      },
    };
  }

  private def(): SceneDef | undefined {
    const s = this.world.scene;
    return s ? SCENES[s.id] : undefined;
  }

  /** The entity an actor stands in for: the player, the elder, the pet or a townsperson. */
  private entityOf(def: SceneDef | undefined, actor: string): Entity | undefined {
    const a = def?.actors[actor];
    if (!a) return undefined;
    const w = this.world;
    if (a.sprite === 'player') return w.player;
    if (a.standsIn === 'elder') return w.elder;
    if (a.standsIn === 'pet') return w.pet;
    if (a.standsIn?.startsWith('folk:')) {
      const id = a.standsIn.slice(5);
      for (const e of w.entities.values()) if (e.folk?.id === id) return e;
    }
    if (a.standsIn?.startsWith('npc:')) {
      const name = a.standsIn.slice(4);
      for (const e of w.entities.values()) if (e.kind === 'npc' && e.name === name) return e;
    }
    return undefined;
  }

  private readonly cast: PanelCast = {
    look: (name) => this.sprites.base[name],
    player: () => this.sprites.of(this.world.player),
  };

  private drawPanels(def: SceneDef, f: SceneFrame): void {
    this.paper.hidden = def.kind !== 'panels';
    if (def.kind !== 'panels') return;
    const g = this.panelCanvas.getContext('2d');
    if (!g) return;
    drawPanel(g, f.panel?.art ?? '', f.panel?.age ?? 0, this.cast);
    // Scaled up by whole pixels to fill as much of the screen as fits.
    const k = Math.max(1, Math.floor(Math.min(innerWidth / PANEL_W, innerHeight / PANEL_H)));
    const w = `${PANEL_W * k}px`;
    if (this.panelCanvas.style.width !== w) {
      this.panelCanvas.style.width = w;
      this.panelCanvas.style.height = `${PANEL_H * k}px`;
    }
  }

  private drawFade(f: SceneFrame): void {
    this.fade.style.background = FADE_COLORS[f.fade.color];
    const a = f.fade.color === 'white' && !settings.flash ? Math.min(f.fade.a, SOFT_WHITE) : f.fade.a;
    this.fade.style.opacity = a.toFixed(3);
  }

  /** A line or caption for this hero: their sex's words and their own name. */
  private words(text: string): string {
    return bySex(text, this.world.progress.appearance.sex === 'f' ? 'f' : 'm');
  }

  private drawCaptions(f: SceneFrame): void {
    const want = f.captions.map((c) => `${c.style}|${c.text}`);
    const have = [...this.captions.children] as HTMLElement[];
    if (have.length !== want.length || have.some((el, i) => el.dataset.key !== want[i])) {
      this.captions.replaceChildren(
        ...f.captions.map((c, i) => {
          const el = h('div', { class: `cs-cap ${c.style}` }, this.words(c.text));
          el.dataset.key = want[i]!;
          return el;
        }),
      );
    }
    [...this.captions.children].forEach((el, i) => {
      const c = f.captions[i]!;
      const a = Math.min(1, c.age / CAPTION_EASE, (c.dur - c.age) / CAPTION_EASE);
      (el as HTMLElement).style.opacity = Math.max(0, a).toFixed(3);
    });
  }

  private drawLine(f: SceneFrame): void {
    const l = f.line;
    this.lineBox.hidden = !l;
    if (!l) return;
    setText(this.nameEl, l.name === PLAYER_NAME ? this.world.player.name : l.name);
    setText(this.textEl, typed(this.words(l.text), l.age, l.dur));
    const e = this.entityOf(this.def(), l.actor);
    const set = e ? this.sprites.of(e) : undefined;
    const key = `${l.actor}|${set?.idle[0] ? 1 : 0}`;
    if (set && key !== this.faceOf) {
      this.faceOf = key;
      drawPortrait(this.face, set);
    }
  }

  /** The camera, the puppets and the scene's effects, handed to the renderer. */
  private stage(def: SceneDef, f: SceneFrame): void {
    if (def.kind !== 'stage') {
      this.renderer.stage = null;
      this.renderer.stageZoom(null);
      return;
    }
    const puppets = new Map<number, Entity>();
    const time = this.world.time;
    for (const [id, a] of Object.entries(f.actors)) {
      const e = this.entityOf(def, id);
      if (!e) continue;
      const pose = SCENE_POSES[a.pose] ?? {};
      const face = a.face;
      const heading = face ? screenHeading(face[0], face[1]) : e.heading;
      const side = face ? screenFacing(face[0], face[1]) : 0;
      const folk = e.folk;
      const puppet: Entity = {
        ...e,
        x: a.x,
        y: a.y,
        z: 0,
        vz: 0,
        heading,
        facing: side > 0 ? 1 : side < 0 ? -1 : e.facing,
        moving: a.moving,
        sprinting: false,
        blocking: false,
        path: null,
        animT: time,
        combatT: 0,
        swingT: 0,
        dashT: 0,
        stunT: 0,
        cast: null,
        meditating: false,
        // Townsfolk show their work poses (fishing, sitting) as themselves; everyone else as an emote.
        emote: a.moving || (folk && pose.act) || (folk && pose.gesture) ? null : (pose.emote ?? null),
        emoteT: a.poseAge,
        emoji: a.emoji?.id ?? null,
        emojiT: a.emoji ? EMOJI_SECONDS - a.emoji.age : 0,
        folk: folk
          ? {
              ...folk,
              indoors: false,
              walking: false,
              attending: false,
              act: (pose.act ?? 'idle') as Activity,
              gesture: pose.gesture ?? null,
            }
          : null,
      };
      puppets.set(e.id, puppet);
    }
    const stage: Stage = {
      focus: f.focus,
      puppets,
      draw: (g, ox, oy, iso, t) => this.drawFx(g, ox, oy, iso, t, f),
    };
    this.renderer.stage = stage;
    this.renderer.stageZoom(f.px);
  }

  /** The scene's effects in hard pixels: the palm's red glow, the shrine's hum. */
  private drawFx(
    g: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    iso: IsoCamera,
    t: number,
    f: SceneFrame,
  ): void {
    for (const e of f.fx) {
      const [sx, sy] = iso.proj(e.at.x, e.at.y);
      const x = Math.round(sx + ox);
      const y = Math.round(sy + oy);
      const fadeIn = Math.min(1, e.age / 0.4, (e.dur - e.age) / 0.4);
      if (fadeIn <= 0) continue;
      g.save();
      if (e.fx === 'palmGlow') {
        // A slow cinnabar pulse at the hand, three or four pixels wide.
        const beat = 0.5 + 0.5 * Math.sin(t * 3.2);
        g.globalAlpha = fadeIn * (0.55 + 0.45 * beat);
        g.fillStyle = '#9a3226';
        g.fillRect(x + 3, y - 20, 4, 3);
        g.fillStyle = '#d65a4a';
        g.fillRect(x + 4, y - 20, 2, 2);
        if (beat > 0.7) {
          g.fillStyle = '#f0b08a';
          g.fillRect(x + 4, y - 20, 1, 1);
        }
      } else if (e.fx === 'shrineHum') {
        // A pale yellow-green breath over the shrine yard, and incense smoke rising.
        g.globalAlpha = fadeIn * 0.35;
        g.fillStyle = '#c9d48a';
        for (let j = -6; j <= 6; j++) {
          const half = Math.round(Math.sqrt(1 - (j / 7) ** 2) * 18);
          for (let i = -half; i <= half; i++)
            if ((i + j + Math.floor(t * 4)) % 2 === 0) g.fillRect(x + i, y + j, 1, 1);
        }
        g.globalAlpha = fadeIn * 0.8;
        for (let k = 0; k < 10; k++) {
          const rise = (t * 6 + k * 3.7) % 24;
          g.fillStyle = k % 3 ? '#d8d2c2' : '#e8e2a8';
          g.fillRect(x - 2 + Math.round(Math.sin(t * 1.7 + k) * 2 + (k % 3)), y - 6 - Math.round(rise), 1, 1);
        }
      }
      g.restore();
    }
  }

  /** No scene: hide everything and give the camera back. */
  private close(): void {
    if (this.root.hidden && !this.renderer.stage) return;
    this.root.hidden = true;
    document.body.classList.remove('in-cutscene');
    this.renderer.stage = null;
    this.renderer.stageZoom(null);
    this.music = null;
    this.holdStart = null;
    this.shownId = null;
    this.lastT = -1;
    this.captions.replaceChildren();
  }

  /** The "go here" marker: over its spot when on screen, else at the screen's edge pointing to it. */
  private syncGuide(): void {
    const g = this.world.story.guide;
    if (!g || !this.playing() || this.windowOpen()) {
      this.guideEl.hidden = true;
      return;
    }
    const [x, y] = this.renderer.worldToScreen(g.x, g.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      this.guideEl.hidden = true;
      return;
    }
    this.guideEl.hidden = false;
    const W = innerWidth;
    const H = innerHeight;
    const bw = this.guideEl.offsetWidth || 48;
    const bh = this.guideEl.offsetHeight || 36;
    // Keep clear of the screen's edges by the marker's own size.
    const mx = bw / 2 + 12;
    const my = bh / 2 + 12;
    // The arrow's tip sits on the spot, the label above it.
    let cx = x;
    let cy = y - bh / 2 - 8;
    let turn = 0;
    if (cx < mx || cx > W - mx || cy < my || cy > H - my) {
      // Off screen: slid along the line from the screen's centre to the spot until it meets the
      // edge, the arrow turned to point that way (it points down when unturned).
      const ox = W / 2;
      const oy = H / 2;
      const dx = x - ox;
      const dy = y - oy;
      const kx = Math.abs(dx) > 1e-3 ? (W / 2 - mx) / Math.abs(dx) : Infinity;
      const ky = Math.abs(dy) > 1e-3 ? (H / 2 - my) / Math.abs(dy) : Infinity;
      const k = Math.min(kx, ky, 1);
      cx = ox + dx * k;
      cy = oy + dy * k;
      turn = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
    }
    cx = Math.min(W - mx, Math.max(mx, cx));
    cy = Math.min(H - my, Math.max(my, cy));
    [cx, cy] = this.clearOfHud(cx, cy, bw, bh, mx, my);
    this.guideEl.style.left = `${Math.round(cx - bw / 2)}px`;
    this.guideEl.style.top = `${Math.round(cy - bh / 2)}px`;
    // Only the arrow turns; the label stays upright.
    this.guideArrow.style.rotate = `${Math.round(turn)}deg`;
  }

  /** Moves the marker's centre out of the HUD (dock, menu buttons, quest tracker, status, minimap) by the shortest way. */
  private clearOfHud(cx: number, cy: number, bw: number, bh: number, mx: number, my: number): [number, number] {
    const W = innerWidth;
    const H = innerHeight;
    const rects: DOMRect[] = [];
    for (const sel of ['#dock', '#pills', '#status', '#mapbox', '#zonecard', '#quest', '#gameMenu']) {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el || el.hidden || !el.offsetParent) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) rects.push(r);
    }
    const hw = bw / 2 + 4;
    const hh = bh / 2 + 4;
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const r of rects) {
        if (cx + hw <= r.left || cx - hw >= r.right || cy + hh <= r.top || cy - hh >= r.bottom) continue;
        const opts: [number, number, number][] = [
          [Math.abs(cx + hw - r.left), r.left - hw, cy],
          [Math.abs(r.right - (cx - hw)), r.right + hw, cy],
          [Math.abs(cy + hh - r.top), cx, r.top - hh],
          [Math.abs(r.bottom - (cy - hh)), cx, r.bottom + hh],
        ];
        opts.sort((a, b) => a[0] - b[0]);
        const best = opts.find((o) => o[1] >= mx && o[1] <= W - mx && o[2] >= my && o[2] <= H - my) ?? opts[0]!;
        cx = best[1];
        cy = best[2];
        moved = true;
      }
      if (!moved) break;
    }
    return [Math.min(W - mx, Math.max(mx, cx)), Math.min(H - my, Math.max(my, cy))];
  }
}
